import type { PartialPath } from 'history';
import { isEmpty, trimStart, trimEnd } from 'utils';
import { ILocation } from 'models/ILocation';

export type IHref = ILocation & {
  search: { [key: string]: string };
  toString(): string;
  toLocal(): string;
  toPath(): PartialPath;
};

const getSearch = ({ search }: ILocation) =>
  search && Object.keys(search).length > 0
    ? '?' +
      Object.entries(search)
        .reduce((prev, [key, value]) => prev + `&${key}=${encodeURIComponent(value)}`, '')
        .substring(1)
    : '';
const getHash = (href: ILocation) =>
  (href.hash && `${href.hash.startsWith('#') ? '' : '#'}${href.hash}`) || '';
const getBasename = (href: ILocation) =>
  (href.protocol && href.host && `${href.protocol}://${href.host}`) || '';

type HrefArgs = HTMLAnchorElement | ILocation | string;
const isString = (args: HrefArgs): args is string => typeof args === 'string';
const isLocation = (args: HTMLAnchorElement | ILocation): args is ILocation =>
  typeof (args as ILocation).search === 'object';
export const createHref = (a: HrefArgs): IHref => {
  const parse = (url: HTMLAnchorElement): ILocation => ({
    href: url.href,
    host: url.host,
    hostname: url.hostname,
    pathname: url.pathname[0] !== '/' ? '/' + url.pathname : url.pathname,
    port: url.port,
    protocol: trimEnd(url.protocol, ':'),
    hash: url.hash,
    live: !/#(.*?)?$/.test(url.href),
    search: url.search
      .replace('?', '')
      .split('&')
      .reduce((prev, curr) => {
        const [key, value] = curr.split('=');
        return key && value ? { ...prev, [key]: decodeURIComponent(value) } : prev;
      }, {})
  });
  const anchor = isString(a)
    ? (() => {
        const url = document.createElement('a');
        url.href = a;
        return parse(url);
      })()
    : !isLocation(a)
    ? parse(a)
    : a;

  const href = !isLocation(anchor) ? parse(anchor) : { ...anchor, search: { ...anchor.search } };
  const toString = () => {
    return trimStart(`${getBasename(href)}${href.pathname}${getSearch(href)}${getHash(href)}`, '/');
  };
  const toLocal = () => {
    return `${href.pathname}${getSearch(href)}${getHash(href)}`;
  };
  return {
    search: {},
    ...href,
    toString,
    toLocal,
    toPath: () => ({ pathname: href.pathname, search: getSearch(href), hash: getHash(href) })
  };
};

export const appendQuery = (url: string, query?: { [key: string]: string }) => {
  if (!query || isEmpty(query)) {
    return url;
  }
  let prefix = !/\?/.test(url) ? '?' : '&';
  return Object.keys(query).reduce((prev, key) => {
    const a = `${prev}${prefix}${key}=${encodeURIComponent(query[key])}`;
    prefix = '&';
    return a;
  });
};
